import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle, ChevronLeft, ChevronRight, PanelRightClose, PanelRightOpen, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { FullLoader } from "@/components/site/Layout";
import { Progress } from "@/components/ui/progress";
import { SecurePlayer } from "@/components/learn/SecurePlayer";

const Sidebar = ({ data, current, done, courseId }) => (
  <aside className="rounded-2xl bg-white border border-line overflow-hidden lg:sticky lg:top-20" data-testid="lesson-sidebar">
    <div className="p-5 border-b border-line">
      <p className="text-sm text-soft mb-2">محتوى الكورس</p>
      <div className="flex items-center gap-3"><Progress value={data.percent} className="h-1.5 bg-purple-100" /><span className="text-xs text-soft" data-testid="course-progress-percent">{data.percent}%</span></div>
    </div>
    <div className="max-h-[65vh] overflow-y-auto ws-scroll p-3 space-y-4">
      {data.modules.map((m, mi) => (
        <div key={m.id}>
          <p className="px-2 py-1 text-xs font-semibold text-soft">الوحدة {mi + 1} — {m.title}</p>
          {m.lessons.map((l) => {
            const active = l.id === current;
            return (
              <Link key={l.id} to={`/learn/${courseId}/${l.id}`} data-testid={`sidebar-lesson-${l.id}`}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active ? "bg-purple-100 text-brand font-medium" : "text-soft hover:bg-purple-50"}`}>
                {done.has(l.id) ? <CheckCircle2 size={17} className="text-emerald-500 shrink-0 mt-0.5" /> : active ? <PlayCircle size={17} className="text-[var(--kh-violet-2)] shrink-0 mt-0.5" /> : <Circle size={17} className="text-[#C9A3EE] shrink-0 mt-0.5" />}
                <span className="flex-1 leading-snug">{l.title}</span>
                {l.duration && <span className="text-[11px] text-soft">{l.duration}</span>}
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  </aside>
);

export default function Learn() {
  const { courseId, lessonId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [done, setDone] = useState(new Set());
  const [showSide, setShowSide] = useState(true);
  useSeo(data?.course?.title);

  useEffect(() => {
    api.get(`/learn/${courseId}`).then((r) => {
      setData(r.data);
      setDone(new Set(r.data.progress.completed_lessons));
    }).catch((e) => { toast.error(errMsg(e)); nav("/dashboard", { replace: true }); });
  }, [courseId, nav]);

  const flat = useMemo(() => (data ? data.modules.flatMap((m) => m.lessons) : []), [data]);
  const currentId = lessonId || data?.progress.last_lesson_id || flat[0]?.id;
  const idx = flat.findIndex((l) => l.id === currentId);
  const lesson = flat[idx];

  useEffect(() => {
    if (currentId && data) api.post(`/progress/${currentId}/visit`).catch(() => {});
  }, [currentId, data]);

  if (!data) return <div className="kh-theme min-h-screen bg-soft"><FullLoader /></div>;
  const percent = flat.length ? Math.round((100 * done.size) / flat.length) : 0;

  const toggle = async () => {
    const completed = !done.has(currentId);
    try {
      const { data: p } = await api.post(`/progress/${currentId}/complete?completed=${completed}`);
      setDone(new Set(p.completed_lessons));
      if (completed) toast.success("عاش! خلصت الدرس");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const go = (i) => flat[i] && nav(`/learn/${courseId}/${flat[i].id}`);

  return (
    <div className="kh-theme min-h-screen bg-soft text-ink" data-testid="learn-page">
      <header className="sticky top-0 z-40 h-14 bg-white/90 backdrop-blur border-b border-line px-4 flex items-center justify-between gap-3">
        <Link to="/dashboard" data-testid="learn-back-btn" className="flex items-center gap-2 text-sm text-soft hover:text-brand shrink-0"><ArrowRight size={18} />لوحتي</Link>
        <p className="font-heading font-semibold truncate text-sm sm:text-base">{data.course.title}</p>
        <button onClick={() => setShowSide(!showSide)} className="hidden lg:block text-soft hover:text-brand" data-testid="toggle-sidebar-btn" aria-label="إظهار/إخفاء القائمة">
          {showSide ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
        </button>
      </header>
      <div className="max-w-[1500px] mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <main className={showSide ? "lg:col-span-8" : "lg:col-span-12"}>
          {lesson ? (
            <>
              <SecurePlayer lessonId={lesson.id} />
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-soft">درس {idx + 1} من {flat.length}</p>
                  <h1 className="font-heading text-2xl font-bold text-ink" data-testid="lesson-title">{lesson.title}</h1>
                </div>
                <button onClick={toggle} data-testid="mark-complete-btn"
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${done.has(lesson.id) ? "bg-emerald-50 text-emerald-700" : "bg-[#FFFFFF] text-[var(--kh-violet)] hover:bg-[#F3E8FF]"}`}>
                  <CheckCircle2 size={18} />{done.has(lesson.id) ? "تم خربشة المحاضرة ✓" : "خربش المحاضرة"}
                </button>
              </div>
              {lesson.description && <p className="mt-4 text-soft leading-relaxed whitespace-pre-line">{lesson.description}</p>}
              <div className="mt-8 flex justify-between border-t border-line pt-5">
                <button disabled={idx <= 0} onClick={() => go(idx - 1)} data-testid="prev-lesson-btn" className="flex items-center gap-1 text-sm text-soft hover:text-brand disabled:opacity-30"><ChevronRight size={18} />السابق</button>
                <button disabled={idx >= flat.length - 1} onClick={() => go(idx + 1)} data-testid="next-lesson-btn" className="flex items-center gap-1 text-sm text-soft hover:text-brand disabled:opacity-30">التالي<ChevronLeft size={18} /></button>
              </div>
            </>
          ) : <p className="text-soft py-20 text-center">لا توجد دروس في هذا الكورس بعد.</p>}
        </main>
        {showSide && <div className="lg:col-span-4"><Sidebar data={{ ...data, percent }} current={currentId} done={done} courseId={courseId} /></div>}
      </div>
    </div>
  );
}
